import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faUndo,
  faCircleChevronRight,
  faCircleChevronLeft,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatTime } from "../../utils/helper";
import html2pdf from "html2pdf.js";
import * as XLSX from "xlsx";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

function SystemLogsTable({ logs = [] }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredSystemLogs, setFilteredSystemLogs] = useState([]);
  const [userType, setUserType] = useState("");
  const [format, setFormat] = useState("");
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const itemsPerPage = 50;
  const tableRef = useRef(null);

  const filterLog = () => {
    if (!Array.isArray(logs)) {
      toast.error("Invalid logs data provided.");
      setFilteredSystemLogs([]);
      return;
    }

    if (dateRange.startDate && !dateRange.endDate) {
      toast.error("Please select both start and end dates.");
      return;
    }

    let filtered = logs;

    if (userType) {
      filtered = filtered.filter(
        (log) => log?.type?.toLowerCase() === userType
      );
    }

    if (dateRange.startDate && dateRange.endDate) {
      const start = new Date(dateRange.startDate).setHours(0, 0, 0, 0);
      const end = new Date(dateRange.endDate).setHours(23, 59, 59, 999);
      filtered = filtered.filter((log) => {
        const logDate = log?.createdAt ? new Date(log.createdAt).getTime() : null;
        return logDate && logDate >= start && logDate <= end;
      });
    }

    setFilteredSystemLogs(filtered);
  };

  const resetFilters = () => {
    setUserType("");
    setFormat("");
    setDateRange({ startDate: null, endDate: null });
    setFilteredSystemLogs(logs);
  };

  const allLogs = useMemo(() => {
    return Array.isArray(filteredSystemLogs) && filteredSystemLogs.length > 0
      ? filteredSystemLogs
      : Array.isArray(logs)
      ? logs
      : [];
  }, [filteredSystemLogs, logs]);

  useEffect(() => {
    console.log("Logs:", logs); // Debug logs
    setFilteredSystemLogs(logs);
  }, [logs]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = allLogs.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(allLogs.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getPageNumbers = () => {
    let startPage = Math.max(1, currentPage - 4);
    let endPage = Math.min(totalPages, startPage + 9);

    if (endPage - startPage < 9) {
      startPage = Math.max(1, endPage - 9);
    }

    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  const userTypes = [
    { id: "", name: "All" },
    { id: "customer", name: "Customer" },
    { id: "admin", name: "Admin" },
  ];

  // Export to PDF
  const exportToPDF = () => {
    if (!allLogs.length) {
      toast.error("No logs available to export.");
      return;
    }

    const fullTableHtml = `
      <div style="width: 100%; padding: 50px; margin-bottom: 50px;">
        <h1 style="font-size: 24px; font-weight: bold;">System Logs</h1>
        <div style="float: right; text-align: right;">
          <p>Date Range: ${
            dateRange.startDate && dateRange.endDate
              ? `${formatTime(dateRange.startDate)} - ${formatTime(
                  dateRange.endDate
                )}`
              : "All"
          }</p>
          <p>User Type: ${userType ? userType.toUpperCase() : "All"}</p>
        </div>
        <div style="width: 100%; height: 50px;"></div>
        <table border="1" style="width: 100%; border-collapse: collapse; border-width: 1px; margin-top: 50px;">
          <thead>
            <tr style="border-width: 1px;">
              <th>ID</th>
              <th>User Type</th>
              <th>User Name</th>
              <th>Action</th>
              <th>Details</th>
              <th>IP Address</th>
              <th>Browser</th>
              <th>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            ${allLogs
              .map(
                (log, i) => `
              <tr>
                <td style="font-size: 13px;">${i + 1}</td>
                <td style="font-size: 13px;">${(log?.type || "").toUpperCase()}</td>
                <td style="font-size: 13px;">${log?.userName || "N/A"}</td>
                <td style="font-size: 13px;">${log?.action || "N/A"}</td>
                <td style="font-size: 13px;">${log?.message || "N/A"}</td>
                <td style="font-size: 13px;">${log?.ip || "N/A"}</td>
                <td style="font-size: 13px;">${log?.browserName || "N/A"}</td>
                <td style="font-size: 13px;">${
                  log?.createdAt ? formatTime(log.createdAt) : "N/A"
                }</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
      </div>
    `;

    const element = document.createElement("div");
    element.innerHTML = fullTableHtml;

    const options = {
      margin: 10,
      filename: "SystemLogs.pdf",
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    html2pdf().from(element).set(options).save();
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!allLogs.length) {
      toast.error("No logs available to export.");
      return;
    }

    const newArray = allLogs.map((log, i) => ({
      ID: i + 1,
      "User Type": (log?.type || "").toUpperCase(),
      "User Name": log?.userName || "N/A",
      Action: log?.action || "N/A",
      Details: log?.message || "N/A",
      "IP Address": log?.ip || "N/A",
      Browser: log?.browserName || "N/A",
      Timestamp: log?.createdAt ? formatTime(log.createdAt) : "N/A",
    }));

    const ws = XLSX.utils.json_to_sheet(newArray);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SystemLogs");
    XLSX.writeFile(wb, "SystemLogs.xlsx");
  };

  return (
    <div className="flex flex-wrap -mx-3">
      <div className="mt-10 mb-5 grid grid-cols-1 gap-4 mx-3 px-3 py-10 w-full bg-white border-0 border-transparent border-solid shadow-soft-xl rounded-2xl bg-clip-border">
        <div className="grid grid-cols-12 gap-4 items-end">
          <div className="col-span-3">
            <label
              htmlFor="userType"
              className="block text-xl font-bold text-primary-dark mb-2"
            >
              User Type
            </label>
            <div className="relative">
              <select
                id="userType"
                name="userType"
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
                className="block appearance-none w-full bg-white border border-gray-300 text-gray-700 py-3 px-4 pr-8 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-gray-500 h-[46px] hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
              >
                {userTypes.map((type, i) => (
                  <option key={i} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg
                  className="fill-current h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="col-span-3">
            <label
              htmlFor="dateRange"
              className="block text-xl font-bold text-primary-dark mb-2"
            >
              Date Range
            </label>
            <DatePicker
              selectsRange
              startDate={dateRange.startDate ? new Date(dateRange.startDate) : null}
              endDate={dateRange.endDate ? new Date(dateRange.endDate) : null}
              onChange={(dates) => {
                const [start, end] = dates;
                console.log("DatePicker selected:", { start, end }); // Debug
                setDateRange({ startDate: start, endDate: end });
              }}
              className="w-full pl-5 py-3 rounded-md border border-gray-300 text-primary shadow-sm focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
            />
          </div>
          <div className="col-span-6 flex flex-col sm:flex-row justify-between items-stretch space-y-2 sm:space-y-0 sm:space-x-2">
            <button
              type="button"
              onClick={filterLog}
              className="px-4 py-2 font-bold text-center bg-gradient-to-tl from-primary to-brand-4 uppercase rounded-lg text-white text-xs shadow-md hover:shadow-lg transition-all duration-200 flex-grow"
            >
              <FontAwesomeIcon icon={faFilter} className="mr-2" />
              Filter
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="px-4 py-2 font-bold text-center bg-gradient-to-tl from-brand-1 to-secondary uppercase rounded-lg text-white text-xs shadow-md hover:shadow-lg transition-all duration-200 flex-grow"
            >
              <FontAwesomeIcon icon={faUndo} className="mr-2" />
              Reset
            </button>
            <select
              id="downloadFormat"
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
            >
              <option value="">Select Format</option>
              <option value="pdf">PDF</option>
              <option value="excel">Excel</option>
            </select>
            <button
              onClick={format === "excel" ? exportToExcel : exportToPDF}
              disabled={!format}
              className="text-white bg-primary hover:bg-primary focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center"
            >
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              Download
            </button>
          </div>
        </div>
      </div>

      <div className="flex-none w-full max-w-full px-3">
        <div className="relative flex flex-col min-w-0 mb-6 break-words bg-white border-0 border-transparent border-solid shadow-soft-xl rounded-2xl bg-clip-border">
          <div className="p-6 pb-0 mb-6 bg-white text-primary text-4xl font-extrabold border-b-0 border-b-solid rounded-t-2xl border-b-transparent">
            <h1>System Logs</h1>
          </div>
          <div className="flex-auto px-0 pt-0 pb-2">
            <div className="p-0 overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden shadow-soft-xl">
                  <div className="max-h-[calc(10*3rem)] overflow-y-auto">
                    {currentItems.length > 0 ? (
                      <table
                        ref={tableRef}
                        className="items-center min-w-full table-auto border-gray-200 text-slate-500"
                      >
                        <thead className="sticky top-0 z-10 bg-brand-2">
                          <tr>
                            <th className="px-6 py-3 pl-2 font-bold text-left uppercase align-middle bg-transparent border-b border-gray-200 shadow-none text-base border-b-solid tracking-none whitespace-nowrap text-white">
                              ID
                            </th>
                            <th className="px-6 py-3 pl-2 font-bold text-left uppercase align-middle bg-transparent border-b border-gray-200 shadow-none text-base border-b-solid tracking-none whitespace-nowrap text-white">
                              User Type
                            </th>
                            <th className="px-6 py-3 pl-2 font-bold text-left uppercase align-middle bg-transparent border-b border-gray-200 shadow-none text-base border-b-solid tracking-none whitespace-nowrap text-white">
                              User Name
                            </th>
                            <th className="px-6 py-3 pl-2 font-bold text-left uppercase align-middle bg-transparent border-b border-gray-200 shadow-none text-base border-b-solid tracking-none whitespace-nowrap text-white">
                              Action
                            </th>
                            <th className="px-6 py-3 pl-2 font-bold text-left uppercase align-middle bg-transparent border-b border-gray-200 shadow-none text-base border-b-solid tracking-none whitespace-nowrap text-white">
                              Details
                            </th>
                            <th className="px-6 py-3 pl-2 font-bold text-left uppercase align-middle bg-transparent border-b border-gray-200 shadow-none text-base border-b-solid tracking-none whitespace-nowrap text-white">
                              IP Address
                            </th>
                            <th className="px-6 py-3 pl-2 font-bold text-left uppercase align-middle bg-transparent border-b border-gray-200 shadow-none text-base border-b-solid tracking-none whitespace-nowrap text-white">
                              Browser
                            </th>
                            <th className="px-6 py-3 pl-2 font-bold text-left uppercase align-middle bg-transparent border-b border-gray-200 shadow-none text-base border-b-solid tracking-none whitespace-nowrap text-white">
                              Timestamp
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.map((log, i) => (
                            <tr key={i}>
                              <td className="p-2 align-middle bg-transparent border-b border-gray-300 whitespace-nowrap shadow-transparent">
                                <p className="mb-0 text-sm font-semibold text-primary">
                                  {i + 1}
                                </p>
                              </td>
                              <td className="p-2 align-middle bg-transparent border-b border-gray-300 whitespace-nowrap shadow-transparent">
                                <p className="mb-0 text-sm font-semibold text-primary">
                                  {(log?.type || "").toUpperCase()}
                                </p>
                              </td>
                              <td className="p-2 align-middle bg-transparent border-b border-gray-300 whitespace-nowrap shadow-transparent">
                                <p className="mb-0 text-sm font-semibold text-primary">
                                  {log?.userName || "N/A"}
                                </p>
                              </td>
                              <td className="p-2 align-middle bg-transparent border-b border-gray-300 whitespace-nowrap shadow-transparent">
                                <p className="mb-0 text-sm font-semibold text-primary">
                                  {log?.action || "N/A"}
                                </p>
                              </td>
                              <td className="p-2 align-middle bg-transparent border-b border-gray-300 whitespace-nowrap shadow-transparent">
                                <p className="mb-0 text-sm font-semibold text-primary">
                                  {log?.message || "N/A"}
                                </p>
                              </td>
                              <td className="p-2 align-middle bg-transparent border-b border-gray-300 whitespace-nowrap shadow-transparent">
                                <p className="mb-0 text-sm font-semibold text-primary">
                                  {log?.ip || "N/A"}
                                </p>
                              </td>
                              <td className="p-2 align-middle bg-transparent border-b border-gray-300 whitespace-nowrap shadow-transparent">
                                <p className="mb-0 text-sm font-semibold text-primary">
                                  {log?.browserName || "N/A"}
                                </p>
                              </td>
                              <td className="p-2 align-middle bg-transparent border-b border-gray-300 whitespace-nowrap shadow-transparent">
                                <p className="mb-0 text-sm font-semibold text-primary">
                                  {log?.createdAt ? formatTime(log.createdAt) : "N/A"}
                                </p>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-center text-primary">No logs available</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full mt-4">
            <div className="flex flex-col sm:flex-row justify-between items-center p-4 bg-white rounded-lg shadow">
              <div className="text-sm font-semibold text-primary mb-2 sm:mb-0">
                Showing {indexOfFirstItem + 1} to{" "}
                {Math.min(indexOfLastItem, allLogs.length)} of {allLogs.length}{" "}
                results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`rounded-md p-1 ${
                    currentPage === 1
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-primary hover:bg-gray-100"
                  }`}
                >
                  <FontAwesomeIcon icon={faCircleChevronLeft} className="text-xl" />
                </button>
                <div className="flex space-x-1">
                  {getPageNumbers().map((number) => (
                    <button
                      key={number}
                      onClick={() => paginate(number)}
                      className={`px-3 py-1 rounded-md text-sm ${
                        currentPage === number
                          ? "bg-primary text-white"
                          : "bg-gray-200 text-primary hover:bg-gray-300"
                      }`}
                    >
                      {number}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`rounded-md p-1 ${
                    currentPage === totalPages
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-primary hover:bg-gray-100"
                  }`}
                >
                  <FontAwesomeIcon icon={faCircleChevronRight} className="text-xl" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

SystemLogsTable.propTypes = {
  logs: PropTypes.arrayOf(
    PropTypes.shape({
      createdBy: PropTypes.string,
      ip: PropTypes.string,
      browserName: PropTypes.string,
      userName: PropTypes.string,
      type: PropTypes.string.isRequired,
      action: PropTypes.string,
      message: PropTypes.string.isRequired,
      createdAt: PropTypes.string,
    })
  ),
};

export default SystemLogsTable;